import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import bindMethods from 'auto-bind';
import first from 'lodash/first';
import isEmpty from 'lodash/isEmpty';

import { setPage } from '../state/navigationSelection';
import { enableToggle } from '../state/sidebar';
import { resetTeamSeasonGameSelection } from '../state/teamSeasonGameSelection';
import { generatePageOptions } from '../utils/generateNavigationOptions';
import { getPageValue } from '../state/selectors/getNavigationValue';
import NavigationItemPicker from './NavigationItemPicker';
import { getDefaultSelections } from '../utils/defaultSelections';
import { getQlikApp } from '../utils/qlik';
import { getQlikAppId } from '../state/selectors/getQlikAppId';
import { addDefaultSelections, removeDefaultSelections } from '../state/defaultSelections';

class PagePicker extends PureComponent {
  constructor(props) {
    super(props);
    bindMethods(this);
  }

  async componentDidUpdate(prevProps) {
    const { getPageValue, pages, qlikAppId } = this.props;
    const previousValue = prevProps.getPageValue.value;
    const currentValue = getPageValue.value;

    if (previousValue !== currentValue) {
      const previousPage = first(prevProps.pages.filter(page => page.pageId === previousValue));
      const currentPage = first(pages.filter(page => page.pageId === currentValue));

      if (previousPage) {
        getDefaultSelections(previousPage.defaultSelection).then((previousDefaultSelections) => {
          this.removeDefaultSelections(prevProps.qlikAppId, previousDefaultSelections);
        });
      }

      if (currentPage) {
        getDefaultSelections(currentPage.defaultSelection).then((defaultSelections) => {
          this.setDefaultSelections(qlikAppId, defaultSelections);
        });
      }
      // Clear selection in redux when `qlikAppId` changes.
      // Otherwise, retain selection across pages.
      const previousQlikAppId = prevProps.qlikAppId;
      const currentQlikAppId = qlikAppId;
      if (previousQlikAppId !== currentQlikAppId) {
        const { resetTeamSeasonGameSelection } = this.props;
        resetTeamSeasonGameSelection();
      }
    }
  }

  async setDefaultSelections(qlikAppId, selections) {
    const { addDefaultSelections } = this.props;
    const keys = Object.keys(selections);

    addDefaultSelections(selections);

    if (!isEmpty(keys)) {
      const app = await getQlikApp(qlikAppId);

      await Promise.all(
        keys.map(async (key) => {
          await app.field(key).selectValues(selections[key], false, false);
        }),
      );
    }
  }

  generateOptions() {
    const { group, purpose, pages } = this.props;
    const options = generatePageOptions(group, purpose.sectionName, pages);
    return options;
  }

  handleChange(value) {
    this.selectPage(value.value);
  }

  async removeDefaultSelections(qlikAppId, selections) {
    const { removeDefaultSelections } = this.props;
    const keys = Object.keys(selections);

    removeDefaultSelections();

    if (!isEmpty(keys)) {
      const app = await getQlikApp(qlikAppId);

      await Promise.all(
        keys.map(async (key) => {
          await app.field(key).clear();
        }),
      );
    }
  }

  selectPage(pageId) {
    const { getPageValue: value, setPage } = this.props;

    if (pageId === value.value) {
      return;
    }

    const { pages, enableToggle } = this.props;
    const page = pages.find(page => page.pageId === pageId);

    setPage(page);
    enableToggle();
  }

  render() {
    const { isLoaded } = this.props;

    if (!isLoaded) {
      return null;
    }

    const { t, getPageValue } = this.props;

    return (
      <NavigationItemPicker
        selectId="NavigationBar.DropDown.Page"
        value={getPageValue}
        options={this.generateOptions()}
        onChange={this.handleChange}
        theme="general"
        placeholder={t('NavigationBar.Placeholders.Page')}
        noOptionsMessage={() => t('NavigationBar.NoOptionsMessage.Page')}
      />
    );
  }
}

PagePicker.propTypes = {
  t: PropTypes.func.isRequired,
  qlikAppId: PropTypes.string,
  isLoaded: PropTypes.bool,
  pages: PropTypes.array,
  group: PropTypes.object,
  purpose: PropTypes.object,
  setPage: PropTypes.func.isRequired,
  addDefaultSelections: PropTypes.func.isRequired,
  removeDefaultSelections: PropTypes.func.isRequired,
  getPageValue: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  resetTeamSeasonGameSelection: PropTypes.func.isRequired, // supplied from Redux
  enableToggle: PropTypes.func.isRequired, // supplied from Redux
};

const mapStateToProps = state => ({
  isLoaded: state.pages.isLoaded,
  qlikAppId: getQlikAppId(state),
  pages: state.pages.pages,
  group: state.navigationSelection.group,
  purpose: state.navigationSelection.purpose,
  getPageValue: getPageValue(state),
});

const mapDispatchToProps = {
  setPage,
  removeDefaultSelections,
  addDefaultSelections,
  resetTeamSeasonGameSelection,
  enableToggle,
};

const TranslatedComponent = withTranslation()(PagePicker);

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(TranslatedComponent);
